import { useCallback, useState } from "react";
import { Alert, AlertTitle, Box, Button, Chip, Paper, Stack, Typography } from "@mui/material";

import { Disclose, Loading, Refusal } from "@lopie/kernel-ui";

import { Heading, Lines, Screenshot, Word } from "./sandboxView.jsx";
import {
  evidenceFailure,
  evidenceRoute,
  evidenceScreenshotRoute,
  evidenceView,
} from "./evidenceReview.js";

// What a decided request was decided on (LO-199), on the request it belongs to.
//
// The sandbox is gone once a request is decided, and with it the live QA report,
// call log and egress log the approver read. The kernel froze a copy of them
// before it wrote the decision, and this shows that copy, read-only: there is no
// control here that writes anything, and the record cannot be changed. Every
// verdict is the kernel's and every caveat the record's, attached to the thing it
// qualifies rather than gathered at the foot of the card.

const WRAPS = { "& code": { overflowWrap: "anywhere" } };

const RUN_WORD = {
  passed: { tone: "permitted" },
  failed: { tone: "refused", word: "failed: a finding about the app" },
  error: { tone: "attention", word: "did not complete: the platform's, not a finding" },
  running: { tone: "stale", word: "still running when it was decided" },
};

function KeptScreenshot({ requestId, runId, path, label, get, eager }) {
  return (
    <Screenshot
      requestId={requestId}
      runId={runId}
      path={path}
      label={label}
      get={get}
      eager={eager}
      routeOf={(id, run, p) => [evidenceScreenshotRoute(id, run, p), {}]}
      failureOf={evidenceFailure}
    />
  );
}

/** One failing check of the kept report: what failed, and how to see it again. */
function KeptFinding({ check, requestId, runId, view, get }) {
  const steps = Array.isArray(check.reproduction) ? check.reproduction : [];
  const unjudged = check.inconclusive === true;
  return (
    <Paper
      variant="outlined"
      role="group"
      aria-label={`${unjudged ? "inconclusive" : "failed"} check at the decision: ${check.title || check.id}`}
      sx={{ p: 1.5, minWidth: 0, ...WRAPS }}
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
          {unjudged ? (
            <Word tone="attention">inconclusive · not a finding about the app</Word>
          ) : (
            <Word tone="refused">failed</Word>
          )}
          <Typography variant="subtitle2" sx={{ overflowWrap: "anywhere", minWidth: 0 }}>
            {check.title || check.id}
          </Typography>
          <Chip size="small" variant="outlined" label={check.persona ? `as ${check.persona}` : "whole run"} />
        </Stack>
        <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>{check.detail}</Typography>
        {steps.length ? (
          <Box>
            <Heading>To reproduce</Heading>
            <Box component="ol" sx={{ m: 0, pl: 3 }}>
              {steps.map((step, i) => (
                <Typography key={i} variant="body2" component="li" sx={{ overflowWrap: "anywhere" }}>
                  {step}
                </Typography>
              ))}
            </Box>
          </Box>
        ) : null}
        {check.screenshot && view.listed(runId, check.screenshot) ? (
          <KeptScreenshot requestId={requestId} runId={runId} path={check.screenshot} label={check.title} get={get} eager />
        ) : null}
      </Stack>
    </Paper>
  );
}

function KeptReport({ view, requestId, get }) {
  const report = view.report;
  if (!report) {
    return (
      <Typography variant="body2" color="text.secondary">{view.noRun}</Typography>
    );
  }
  const runId = String(report.run?.run_id || "");
  const word = RUN_WORD[report.kind] || RUN_WORD.error;
  const passing = report.totals.passed;
  const shots = view.screenshots.filter((f) => f.run_id === runId);
  return (
    <Stack spacing={1.25}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
        <Word
          tone={word.tone}
          title="The kernel's verdict on the run, copied at the decision. It is not a new judgement."
        >
          {report.kind === "passed" ? report.label : word.word}
        </Word>
        <Typography variant="body2" color="text.secondary" sx={{ ...WRAPS }}>
          run <code>{runId}</code> · {report.totals.failed} failed · {report.totals.skipped} skipped ·{" "}
          {passing} passed{report.totals.checks ? ` of ${report.totals.checks} checks` : ""}
        </Typography>
      </Stack>
      {report.error ? (
        <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
          {report.error.reason}: {report.error.detail}
        </Typography>
      ) : null}
      {report.findings.map((check) => (
        <KeptFinding
          key={`${check.persona}:${check.id}`}
          check={check}
          requestId={requestId}
          runId={runId}
          view={view}
          get={get}
        />
      ))}
      {shots.length ? (
        <Disclose
          summary="The screenshots the run took, kept with the decision."
          label="Screenshots"
          count={shots.length}
        >
          <Stack spacing={1.5}>
            {shots.map((s) => (
              <KeptScreenshot key={s.path} requestId={requestId} runId={runId} path={s.path} label={s.path} get={get} />
            ))}
          </Stack>
        </Disclose>
      ) : null}
      {view.asRequested ? (
        <Typography variant="body2" color="text.secondary" sx={{ ...WRAPS }}>
          The run for the request as it was asked, before it was narrowed, is kept beside it:{" "}
          <code>{view.asRequested.runId}</code> {view.asRequested.status}. The decision did not land on it.
        </Typography>
      ) : null}
    </Stack>
  );
}

function KeptCalls({ calls }) {
  if (!calls.available) {
    return <Typography variant="body2" color="text.secondary">{calls.detail}</Typography>;
  }
  return (
    <Stack spacing={0.5}>
      <Typography variant="body2">
        {calls.total} {calls.total === 1 ? "call" : "calls"} recorded · {calls.allowed} allowed ·{" "}
        {calls.empty} empty · {calls.refused} refused
        {calls.unused ? ` · ${calls.unused} requested ${calls.unused === 1 ? "grant" : "grants"} no call used` : ""}
      </Typography>
      <Disclose
        summary={calls.doesNotProve[0] || "The call log is best-effort."}
        label="By persona"
        count={calls.byPersona.length}
        align="row"
      >
        <Stack spacing={0.25} component="ul" sx={{ m: 0, pl: 2.5 }}>
          {calls.byPersona.map((p) => (
            <Typography key={p.name} variant="body2" component="li" sx={{ overflowWrap: "anywhere" }}>
              <code>{p.name}</code>: {p.total} {p.total === 1 ? "call" : "calls"}, {p.refused} refused
            </Typography>
          ))}
        </Stack>
      </Disclose>
    </Stack>
  );
}

function KeptEgress({ egress }) {
  if (!egress.available) {
    return <Typography variant="body2" color="text.secondary">{egress.detail}</Typography>;
  }
  const gateway = egress.gateway;
  return (
    <Stack spacing={0.75}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
        <Word
          tone={gateway?.healthy ? "permitted" : "attention"}
          title={gateway?.detail || "The record does not say whether the gateway was working."}
        >
          {gateway?.healthy
            ? "gateway working at the decision"
            : `gateway not confirmed working at the decision${gateway?.state ? ` (${String(gateway.state).replace(/_/g, " ")})` : ""}`}
        </Word>
        {egress.reading?.detail ? (
          <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
            {egress.reading.detail}
          </Typography>
        ) : null}
      </Stack>
      {egress.hosts.length ? (
        <Stack component="ul" aria-label="hosts contacted, as kept" spacing={0.25} sx={{ m: 0, pl: 2.5 }}>
          {egress.hosts.map((h) => (
            <Typography key={`${h.host}:${h.allowed}`} variant="body2" component="li" sx={{ overflowWrap: "anywhere" }}>
              <code>{h.host}</code>: {h.allowed ? "let through" : "blocked"}, {h.count}{" "}
              {h.count === 1 ? "attempt" : "attempts"}
            </Typography>
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">No host was recorded.</Typography>
      )}
    </Stack>
  );
}

/**
 * "What this was approved on" (or rejected on), for one decided request that
 * named a build. It reads the request when the reader asks, once, and shows the
 * record the kernel kept, or says in the kernel's words why there is none.
 * Read-only. Not read on its own: a queue of decided requests is not a reason
 * to read each of them again, and a card that has just been decided is not
 * polled (approve-gate.mjs holds that).
 */
export function DecisionEvidence({ request, get }) {
  const requestId = request.request_id;
  const [state, setState] = useState({ status: "idle" });
  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const body = await get(evidenceRoute(requestId));
      setState({ status: "ok", view: evidenceView(body?.evidence) });
    } catch (e) {
      setState({ status: "failed", error: e });
    }
  }, [get, requestId]);
  const fallback = request.state === "rejected" ? "What this was rejected on" : "What this was approved on";
  if (state.status === "idle") {
    return (
      <Box component="section" aria-label={fallback}>
        <Heading>{fallback}</Heading>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
          <Typography variant="body2" color="text.secondary">
            The QA run, call log and egress log it relied on, frozen before the sandbox was removed. Read-only.
          </Typography>
          <Button size="small" variant="outlined" onClick={load}>Show the record</Button>
        </Stack>
      </Box>
    );
  }
  if (state.status === "loading") {
    return (
      <Box component="section" aria-label={fallback}>
        <Heading>{fallback}</Heading>
        <Loading label={`Reading ${fallback.toLowerCase()}`} minHeight={60} size={20} />
      </Box>
    );
  }
  if (state.status === "failed") {
    return (
      <Box component="section" aria-label={fallback}>
        <Heading>{fallback}</Heading>
        <Refusal failure={state.error} actor="This console" errorTitle="The record could not be read" onRetry={load} />
      </Box>
    );
  }
  const view = state.view;
  if (!view.kept) {
    return (
      <Box component="section" aria-label={fallback}>
        <Heading>{fallback}</Heading>
        <Alert severity="info" variant="outlined">
          <AlertTitle>No record of what this was decided on is kept</AlertTitle>
          {view.detail}
        </Alert>
      </Box>
    );
  }
  return (
    <Box component="section" aria-label={view.heading} sx={{ minWidth: 0, ...WRAPS }}>
      <Heading>{view.heading}</Heading>
      <Stack spacing={1.5}>
        <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
          Build <code>{view.buildId}</code> under manifest <code title={view.manifestHash}>{view.shortHash}</code>, decided
          by <code>{view.decidedBy}</code> at {view.decidedAt}
          {view.landed ? `, ${view.landed}` : ""}. Frozen from the sandbox before it was removed, and read-only: nothing
          can change it.
        </Typography>

        <KeptReport view={view} requestId={requestId} get={get} />

        <Box>
          <Heading>Kernel calls the sandbox recorded</Heading>
          <KeptCalls calls={view.calls} />
        </Box>

        <Box>
          <Heading>Hosts its server function tried</Heading>
          <KeptEgress egress={view.egress} />
        </Box>

        <Disclose
          summary={view.doesNotProve[0] || "A copy of what the sandbox recorded, not a new judgement."}
          label="What it does not prove"
          count={view.doesNotProve.length}
          align="row"
        >
          <Lines lines={view.doesNotProve.slice(1)} />
        </Disclose>
        <Disclose
          summary={view.retention.record || "Kept with the request."}
          label="Where it is kept"
          align="row"
        >
          <Lines lines={[view.retention.screenshots, view.retention.not_kept].filter(Boolean)} />
        </Disclose>
        {view.trimmed ? (
          <Typography variant="caption" color="text.secondary">
            The record had to be cut to fit: the words of some passing checks were shortened. Every check&apos;s
            verdict and every failure&apos;s steps are kept.
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
}
